/**
 * React hook for template API operations
 * Provides easy-to-use functions for template management
 * - Lecture: Templates locaux uniquement
 * - Synchronisation: Active - Envoie les templates vers le backend
 */

import { useState, useCallback } from 'react';
// IMPORT D'API DÉSACTIVÉ - Plus aucun appel API
// import TemplateApiService, { TemplateData, TemplateResponse, TemplatesListResponse } from '../services/templateApi';
import { TemplateDiscoveryService, TemplateData } from '../services/templateService';

// Types locaux pour éviter l'import de templateApi
export interface TemplateResponse {
  status: string;
  message: string;
  template: TemplateData;
}

export interface TemplatesListResponse {
  status: string;
  templates: TemplateData[];
  count: number;
}

export interface UseTemplateApiReturn {
  // State
  templates: TemplateData[];
  loading: boolean;
  error: string | null;
  lastOperation: string | null;

  // Actions
  addTemplate: (templateData: TemplateData) => Promise<TemplateResponse>;
  getTemplates: () => Promise<TemplatesListResponse>;
  getTemplate: (templateId: string) => Promise<TemplateResponse>;
  updateTemplate: (templateId: string, templateData: Partial<TemplateData>) => Promise<TemplateResponse>;
  deleteTemplate: (templateId: string) => Promise<void>;
  syncTemplates: (templates: TemplateData[]) => Promise<void>;
  
  // Utilities
  clearError: () => void;
  refreshTemplates: () => Promise<void>;
}

/**
 * Hook for template API operations
 */
export const useTemplateApi = (): UseTemplateApiReturn => {
  const [templates, setTemplates] = useState<TemplateData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOperation, setLastOperation] = useState<string | null>(null);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Add a new template
   * DÉSACTIVÉ - Plus aucun appel API
   */
  const addTemplate = useCallback(async (_templateData: TemplateData): Promise<TemplateResponse> => {
    // AJOUT DÉSACTIVÉ - Ne fait rien (templates en local uniquement)
    console.log('⚠️ addTemplate désactivé - templates locaux uniquement');
    setLastOperation('Template addition disabled - local templates only');
    throw new Error('Template addition disabled - using local templates only');
  }, []);

  /**
   * Get all templates
   * UTILISE UNIQUEMENT LES TEMPLATES LOCAUX (codés en dur dans le frontend)
   * Aucun appel API pour charger la liste initiale
   */
  const getTemplates = useCallback(async (): Promise<TemplatesListResponse> => {
    setLoading(true);
    setError(null);
    setLastOperation('Loading templates...');

    try {
      // Charger UNIQUEMENT les templates locaux (source de vérité)
      const localTemplates = TemplateDiscoveryService.discoverTemplates();
      
      // Afficher immédiatement les templates locaux
      setTemplates(localTemplates);
      setLastOperation(`Loaded ${localTemplates.length} local templates`);
      
      console.log(`✅ ${localTemplates.length} templates chargés depuis le frontend (codés en dur)`);
      
      // Retourner les templates locaux
      return {
        status: 'success',
        templates: localTemplates,
        count: localTemplates.length
      };
      
    } catch (err) {
      // Dernier fallback : templates locaux
      const localTemplates = TemplateDiscoveryService.discoverTemplates();
      setTemplates(localTemplates);
      setError(null); // Ne pas afficher d'erreur car on utilise les templates locaux
      setLastOperation(`Loaded ${localTemplates.length} local templates`);
      
      console.warn('⚠️ Utilisation des templates locaux (fallback)');
      
      return {
        status: 'success',
        templates: localTemplates,
        count: localTemplates.length
      };
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Get a specific template
   * DÉSACTIVÉ - Utilise uniquement les templates locaux
   */
  const getTemplate = useCallback(async (templateId: string): Promise<TemplateResponse> => {
    setLoading(true);
    setError(null);
    setLastOperation(`Fetching template ${templateId}...`);

    try {
      // Récupérer UNIQUEMENT depuis les templates locaux
      const localTemplates = TemplateDiscoveryService.discoverTemplates();
      const localTemplate = localTemplates.find(t => t.template_id === templateId);
      
      if (localTemplate) {
        setLastOperation(`Template ${templateId} loaded from local templates`);
        setLoading(false);
        return {
          status: 'success',
          message: 'Template loaded from local templates',
          template: localTemplate
        };
      }
      
      // Template non trouvé dans les templates locaux
      throw new Error(`Template ${templateId} not found in local templates`);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      setLastOperation(`Failed to fetch template ${templateId}`);
      setLoading(false);
      throw err;
    }
  }, []);

  /**
   * Update a template
   * DÉSACTIVÉ - Plus aucun appel API
   */
  const updateTemplate = useCallback(async (
    _templateId: string, 
    _templateData: Partial<TemplateData>
  ): Promise<TemplateResponse> => {
    // MISE À JOUR DÉSACTIVÉE - Ne fait rien (templates en local uniquement)
    console.log('⚠️ updateTemplate désactivé - templates locaux uniquement');
    setLastOperation('Template update disabled - local templates only');
    throw new Error('Template update disabled - using local templates only');
  }, []);

  /**
   * Delete a template
   * DÉSACTIVÉ - Plus aucun appel API
   */
  const deleteTemplate = useCallback(async (_templateId: string): Promise<void> => {
    // SUPPRESSION DÉSACTIVÉE - Ne fait rien (templates en local uniquement)
    console.log('⚠️ deleteTemplate désactivé - templates locaux uniquement');
    setLastOperation('Template deletion disabled - local templates only');
    throw new Error('Template deletion disabled - using local templates only');
  }, []);

  /**
   * Sync multiple templates
   * ACTIVE - Envoie les templates locaux vers le backend
   */
  const syncTemplates = useCallback(async (templatesToSync: TemplateData[]): Promise<void> => {
    setLoading(true);
    setError(null);
    setLastOperation(`Synchronisation de ${templatesToSync.length} templates vers le backend...`);

    try {
      // Utiliser la variable d'environnement VITE_API_URL avec fallback pour localhost
      const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001/api';
      
      // Préparer les données au format attendu par le backend
      const templatesPayload = templatesToSync.map(template => ({
        template_id: template.template_id,
        component_name: template.component_name,
        file_path: template.file_path,
        category: template.category || 'unknown',
        industry: template.industry || 'general',
        style: template.style || 'professional',
        name: template.name || template.component_name,
        description: template.description || '',
        tags: template.tags || [],
        is_active: template.is_active !== undefined ? template.is_active : true
      }));

      console.log(`🔄 Envoi de ${templatesPayload.length} templates vers ${API_BASE_URL}/templates/sync/`);

      // Préparer les headers - ne pas envoyer Idempotency-Key en local (localhost)
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      // Ajouter Idempotency-Key uniquement si on n'est pas en localhost
      if (!API_BASE_URL.includes('localhost') && !API_BASE_URL.includes('127.0.0.1')) {
        const idempotencyKey = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
        headers['Idempotency-Key'] = idempotencyKey;
      }

      const response = await fetch(`${API_BASE_URL}/templates/sync/`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ templates: templatesPayload }),
        // Mode 'cors' par défaut, mais on gère les erreurs CORS explicitement
        mode: 'cors',
      });

      // Vérifier si c'est une erreur CORS avant de lire la réponse
      if (!response.ok && response.status === 0) {
        // Erreur CORS probable (status 0 = network error, souvent CORS)
        throw new Error('CORS_ERROR: Le serveur bloque les requêtes depuis cette origine. Vérifiez la configuration CORS du backend.');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      console.log(`✅ Synchronisation réussie: ${data.synced_count || 0}/${data.total_templates || templatesPayload.length} templates synchronisés`);
      
      if (data.errors && data.errors.length > 0) {
        console.warn('⚠️ Erreurs lors de la synchronisation:', data.errors);
      }

      setLastOperation(`Synchronisation réussie: ${data.synced_count || 0} templates envoyés au backend`);
      
      // Rafraîchir la liste des templates après synchronisation
      await getTemplates();
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      
      // Détecter spécifiquement les erreurs CORS
      if (errorMessage.includes('CORS') || errorMessage.includes('Failed to fetch') || errorMessage.includes('blocked by CORS')) {
        const corsError = '❌ Erreur CORS: Le serveur backend envoie plusieurs valeurs pour Access-Control-Allow-Origin (*, http://localhost:5174). Corrigez la configuration CORS côté backend pour n\'envoyer qu\'une seule valeur.';
        setError(corsError);
        setLastOperation('⚠️ Erreur CORS - Vérifiez la configuration backend');
        console.error('❌ Erreur CORS détectée:', corsError);
        console.warn('💡 Solution: Modifiez settings.py pour ajouter http://localhost:5174 dans CORS_ALLOWED_ORIGINS et vérifiez Nginx ne duplique pas les headers');
        throw new Error(corsError);
      }
      
      setError(errorMessage);
      setLastOperation(`Échec de la synchronisation: ${errorMessage}`);
      console.error('❌ Erreur synchronisation:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [getTemplates]);

  /**
   * Refresh templates list
   */
  const refreshTemplates = useCallback(async (): Promise<void> => {
    await getTemplates();
  }, [getTemplates]);

  return {
    // State
    templates,
    loading,
    error,
    lastOperation,

    // Actions
    addTemplate,
    getTemplates,
    getTemplate,
    updateTemplate,
    deleteTemplate,
    syncTemplates,
    
    // Utilities
    clearError,
    refreshTemplates,
  };
};

export default useTemplateApi;
